import React, { useState } from 'react'
import { Box, Chip, Collapse, Grid, Paper, Typography } from '@mui/material'
import { useTheme } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import FolderIcon from '@material-ui/icons/Folder'
import MeetingRoomIcon from '@material-ui/icons/MeetingRoom'

import {
    AreaPreview,
    FolderNode,
    UnitConfigPreview,
} from '../../../hooks/useSheetOnboard'

const cardStyle = (theme: {
    spacing: (value: number) => number
    palette: { divider: string }
}): React.CSSProperties => ({
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
})

export const countFolderTree = (nodes: FolderNode[]) => {
    let folders = 0
    let units = 0
    const walk = (node: FolderNode) => {
        folders += 1
        units += node.units.length
        node.children.forEach(walk)
    }
    nodes.forEach(walk)
    return { folders, units }
}

const countUnitsIn = (node: FolderNode): number =>
    node.units.length +
    node.children.reduce((sum, child) => sum + countUnitsIn(child), 0)

const UnitRow = ({ unit }: { unit: FolderNode['units'][number] }) => {
    const theme = useTheme()
    return (
        <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            style={{
                gap: 12,
                padding: '8px 12px',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                backgroundColor: '#FFFFFF',
            }}
        >
            <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, minWidth: 0 }}
            >
                <MeetingRoomIcon
                    style={{
                        fontSize: 16,
                        color: theme.palette.text.secondary,
                    }}
                />
                <Typography variant="body2" style={{ fontWeight: 600 }} noWrap>
                    {unit.name}
                </Typography>
            </Box>
            {unit.unit_config ? (
                <Chip
                    size="small"
                    label={unit.unit_config}
                    variant="outlined"
                    style={{ height: 22, fontSize: 12 }}
                />
            ) : null}
        </Box>
    )
}

const FolderTreeNode = ({
    node,
    depth = 0,
}: {
    node: FolderNode
    depth?: number
}) => {
    const theme = useTheme()
    const [open, setOpen] = useState(true)
    const unitTotal = countUnitsIn(node)
    const hasChildren = node.children.length > 0 || node.units.length > 0
    const isRoot = depth === 0

    return (
        <Box
            style={
                isRoot
                    ? {
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 10,
                          overflow: 'hidden',
                          marginBottom: 12,
                          backgroundColor: '#F7F9F8',
                      }
                    : {
                          marginTop: 8,
                      }
            }
        >
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{
                    padding: isRoot ? '10px 14px' : '6px 0',
                    cursor: hasChildren ? 'pointer' : 'default',
                    backgroundColor: isRoot ? '#EEF3F1' : 'transparent',
                    borderBottom:
                        isRoot && open
                            ? `1px solid ${theme.palette.divider}`
                            : 'none',
                }}
                onClick={() => hasChildren && setOpen((value) => !value)}
            >
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                    {hasChildren ? (
                        open ? (
                            <ExpandLessIcon style={{ fontSize: 18 }} />
                        ) : (
                            <ExpandMoreIcon style={{ fontSize: 18 }} />
                        )
                    ) : null}
                    <FolderIcon
                        style={{
                            fontSize: 18,
                            color: theme.palette.warning.dark,
                        }}
                    />
                    <Typography variant="body2" style={{ fontWeight: 700 }}>
                        {node.name}
                    </Typography>
                </Box>
                <Chip
                    size="small"
                    label={unitTotal === 1 ? '1 unit' : `${unitTotal} units`}
                    style={{ height: 22, fontSize: 12 }}
                />
            </Box>
            <Collapse in={open}>
                <Box
                    style={{
                        padding: isRoot ? 12 : '0 0 0 16px',
                        borderLeft: isRoot
                            ? 'none'
                            : `2px solid ${theme.palette.divider}`,
                    }}
                >
                    {node.children.map((child) => (
                        <FolderTreeNode
                            key={child.id}
                            node={child}
                            depth={depth + 1}
                        />
                    ))}
                    {node.units.length > 0 && (
                        <Grid
                            container
                            spacing={1}
                            style={{ marginTop: node.children.length ? 8 : 0 }}
                        >
                            {node.units.map((unit) => (
                                <Grid item xs={12} sm={6} md={4} key={unit.id}>
                                    <UnitRow unit={unit} />
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </Box>
            </Collapse>
        </Box>
    )
}

export const AreaConfigPreviewList = ({
    areas,
    emptyHint,
}: {
    areas: AreaPreview[]
    emptyHint?: string
}) => {
    const theme = useTheme()

    if (areas.length === 0) {
        return (
            <Paper elevation={0} style={cardStyle(theme)}>
                <Typography variant="body2" color="textSecondary">
                    {emptyHint ||
                        'Upload the area sheet to review the area configs before continuing.'}
                </Typography>
            </Paper>
        )
    }

    return (
        <Paper elevation={0} style={cardStyle(theme)}>
            <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 4 }}
            >
                Review area configs
            </Typography>
            <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 12 }}
            >
                {areas.length} area{areas.length !== 1 ? 's' : ''} created.
                Confirm these look right before going to units.
            </Typography>
            <Grid container spacing={1}>
                {areas.map((area) => (
                    <Grid item xs={12} sm={6} md={4} key={area.id}>
                        <Box
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                            style={{
                                gap: 8,
                                padding: theme.spacing(1.25, 1.5),
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                                backgroundColor: '#F7F9F8',
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                                noWrap
                            >
                                {area.name}
                            </Typography>
                            <Chip
                                size="small"
                                label={
                                    area.occupiable ? 'Occupiable' : 'Common'
                                }
                                color={area.occupiable ? 'primary' : 'default'}
                                variant={
                                    area.occupiable ? 'filled' : 'outlined'
                                }
                            />
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    )
}

export const UnitConfigPreviewList = ({
    unitConfigs,
    emptyHint,
}: {
    unitConfigs: UnitConfigPreview[]
    emptyHint?: string
}) => {
    const theme = useTheme()

    if (unitConfigs.length === 0) {
        return (
            <Paper elevation={0} style={cardStyle(theme)}>
                <Typography variant="body2" color="textSecondary">
                    {emptyHint ||
                        'Upload the unit sheet to review unit types and their area recipe before continuing.'}
                </Typography>
            </Paper>
        )
    }

    return (
        <Paper elevation={0} style={cardStyle(theme)}>
            <Typography
                variant="subtitle2"
                style={{ fontWeight: 600, marginBottom: 4 }}
            >
                Review unit types
            </Typography>
            <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 12 }}
            >
                {unitConfigs.length} unit type
                {unitConfigs.length !== 1 ? 's' : ''} created. Confirm how many
                of each area type belong to each unit type.
            </Typography>
            <Grid container spacing={1}>
                {unitConfigs.map((unitConfig) => (
                    <Grid item xs={12} sm={6} key={unitConfig.id}>
                        <Box
                            style={{
                                padding: theme.spacing(1.5),
                                border: `1px solid ${theme.palette.divider}`,
                                borderRadius: 8,
                                backgroundColor: '#F7F9F8',
                                height: '100%',
                            }}
                        >
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {unitConfig.name}
                            </Typography>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                style={{ marginBottom: 8 }}
                            >
                                {unitConfig.bed_count ?? 0} bed ·{' '}
                                {unitConfig.bath_count ?? 0} bath
                            </Typography>
                            {unitConfig.areas.length === 0 ? (
                                <Typography
                                    variant="caption"
                                    color="textSecondary"
                                >
                                    No area types mapped
                                </Typography>
                            ) : (
                                unitConfig.areas.map((area) => (
                                    <Box
                                        key={area.name}
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        style={{
                                            gap: 12,
                                            padding: '4px 0',
                                            borderTop: `1px solid ${theme.palette.divider}`,
                                        }}
                                    >
                                        <Box
                                            display="flex"
                                            alignItems="center"
                                            style={{ gap: 8, minWidth: 0 }}
                                        >
                                            <Typography variant="body2" noWrap>
                                                {area.name}
                                            </Typography>
                                            <Chip
                                                size="small"
                                                label={
                                                    area.occupiable === false
                                                        ? 'Common'
                                                        : 'Occupiable'
                                                }
                                                variant="outlined"
                                                color={
                                                    area.occupiable === false
                                                        ? 'default'
                                                        : 'primary'
                                                }
                                                style={{
                                                    height: 20,
                                                    fontSize: 11,
                                                }}
                                            />
                                        </Box>
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 600 }}
                                        >
                                            {area.count}
                                        </Typography>
                                    </Box>
                                ))
                            )}
                        </Box>
                    </Grid>
                ))}
            </Grid>
        </Paper>
    )
}

export const BuildingPreviewTree = ({
    folders,
    emptyHint,
}: {
    folders: FolderNode[]
    emptyHint?: string
}) => {
    const theme = useTheme()
    const { folders: folderCount, units } = countFolderTree(folders)

    if (folders.length === 0) {
        return (
            <Paper elevation={0} style={cardStyle(theme)}>
                <Typography variant="body2" color="textSecondary">
                    {emptyHint ||
                        'Upload the infrastructure sheet to review folders and units before finishing.'}
                </Typography>
            </Paper>
        )
    }

    return (
        <Paper elevation={0} style={cardStyle(theme)}>
            <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                style={{ gap: 12, marginBottom: 4, flexWrap: 'wrap' }}
            >
                <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
                    Review buildings and units
                </Typography>
                <Box display="flex" style={{ gap: 8 }}>
                    <Chip
                        size="small"
                        icon={<FolderIcon style={{ fontSize: 16 }} />}
                        label={
                            folderCount === 1
                                ? '1 folder'
                                : `${folderCount} folders`
                        }
                    />
                    <Chip
                        size="small"
                        icon={<MeetingRoomIcon style={{ fontSize: 16 }} />}
                        label={units === 1 ? '1 unit' : `${units} units`}
                    />
                </Box>
            </Box>
            <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginBottom: 12 }}
            >
                Confirm each building, folder, and unit type looks right before
                finishing.
            </Typography>
            {folders.map((folder) => (
                <FolderTreeNode key={folder.id} node={folder} />
            ))}
        </Paper>
    )
}
